/**
 * Zod schemas for package-approval domain — GH-236.
 *
 * Exports:
 *   approvalProvenanceSchema    — validates the JSON snapshot stored in package_approvals.provenance
 *   createApprovalInputSchema   — validates POST /api/onboarding-packages/:id/approvals body
 *   assignReviewerInputSchema   — validates PATCH /api/onboarding-packages/:id/reviewer body
 *   approvalRowSchema           — validates a full ApprovalRow from the API response
 *
 * Design notes:
 *   - notes: optional, nullable, max 1000 chars (enforced here + at service layer).
 *   - reviewerUserId: non-empty string OR null (empty string rejected — use null to clear).
 *   - provenance.score ∈ [0,1]; ambiguityCount ≥ 0.
 *   - approvalRowSchema re-uses approvalProvenanceSchema for the nested object.
 */

import { z } from 'zod'

// ── Provenance snapshot schema ─────────────────────────────────────────────────

/**
 * Validates the JSON provenance snapshot stored at approval time.
 *
 * score and ambiguityCount are nullable:
 *   - null means the value could not be computed at approval time (D1 hiccup,
 *     schema drift, or any thrown error in the snapshot capture path).
 *   - null is NOT an error sentinel for schema validation — it is an honest
 *     "unknown at snapshot time" marker. The approval row itself is valid.
 *   - Readers must handle null gracefully (treat as unknown, not as 0).
 */
export const approvalProvenanceSchema = z.object({
  /** Completeness score at approval time ∈ [0,1], or null if unavailable. */
  score: z.number().min(0).max(1).nullable(),
  /** Blocker codes at approval time (free-form strings). */
  blockers: z.array(z.string()),
  /** Count of ambiguous items at approval time (≥ 0), or null if unavailable. */
  ambiguityCount: z.number().int().min(0).nullable(),
  /** Raw completeness snapshot — untyped, preserved as-is. */
  completenessSnapshot: z.unknown(),
})

export type ApprovalProvenanceSchema = z.infer<typeof approvalProvenanceSchema>

// ── Create approval input schema ───────────────────────────────────────────────

/**
 * Input body for POST /api/onboarding-packages/:id/approvals.
 *
 * notes is optional and nullable; when provided must not exceed 1000 chars.
 * All other approval fields (approverUserId, provenance snapshot, etc.) are
 * derived server-side from session + current package state.
 */
export const createApprovalInputSchema = z.object({
  /** Optional reviewer notes, max 1000 chars. null explicitly allowed to clear. */
  notes: z.string().max(1000).nullable().optional(),
})

export type CreateApprovalInputParsed = z.infer<typeof createApprovalInputSchema>

// ── Assign reviewer input schema ───────────────────────────────────────────────

/**
 * Input body for PATCH /api/onboarding-packages/:id/reviewer.
 *
 * reviewerUserId must be a non-empty string to assign, or null to clear.
 * Empty string is rejected — callers must use null to unassign.
 */
export const assignReviewerInputSchema = z.object({
  /** User ID to assign as reviewer, or null to clear the assignment. */
  reviewerUserId: z.string().min(1).nullable(),
})

export type AssignReviewerInputParsed = z.infer<typeof assignReviewerInputSchema>

// ── Full approval row schema ───────────────────────────────────────────────────

/**
 * Validates a full ApprovalRow as returned by the API.
 * Used for response-body validation in tests and client-side type narrowing.
 */
export const approvalRowSchema = z.object({
  /** UUID primary key. */
  id: z.string().min(1),
  /** FK → onboarding_packages.id */
  packageId: z.string().min(1),
  /** Tenant scope. */
  tenantId: z.string().min(1),
  /** FK → users.id */
  approverUserId: z.string().min(1),
  /** Display name of approver; optional, nullable. */
  approverName: z.string().nullable().optional(),
  /** Email of approver; optional, nullable. */
  approverEmail: z.string().nullable().optional(),
  /** Lifecycle state snapshot at approval time. */
  lifecycleAtApproval: z.string().min(1),
  /** Optional reviewer notes; max 1000 chars. */
  notes: z.string().max(1000).nullable().optional(),
  /** Trust-signal snapshot captured at approval time. */
  provenance: approvalProvenanceSchema,
  /** Unix ms timestamp. */
  createdAt: z.number().int(),
})

export type ApprovalRowSchema = z.infer<typeof approvalRowSchema>
